/**
 * POS Reports — Sprint 7 (Premium UX Sprint)
 *
 * Route: /pos/reports  (feature-gated on `pos`, requiredRole="admin")
 *
 * Design intent:
 *   • Keep every existing working POS report accessible. In the
 *     current build the actual X / Z / till / shift close reports
 *     surface from the terminal itself (FloorView), so this page
 *     links back to the terminal for those. Petty Cash reports are
 *     already available at /admin/petty-cash/reports and appear here
 *     as a card too.
 *   • Add a clearly-labelled "Advanced Analytics — Coming Soon"
 *     section: 14 catalogue cards, no fake data, no backend calls.
 *   • Sits inside the RestroPulse admin shell (Layout.js supplies the
 *     top nav). Visual system matches the rest of the platform.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  BarChart3, ArrowLeft, ExternalLink, Wallet,
  Utensils, TrendingUp, Sun, Truck, CalendarDays,
  CalendarClock, Star, IndianRupee, LineChart,
  UserRound, Leaf, Grid3x3, ShieldCheck, Timer, Sparkles,
} from 'lucide-react';

// ─── Existing working reports ─────────────────────────────────────────
const WORKING_REPORTS = [
  {
    id: 'z-report',
    title: 'X / Z Report',
    hint: 'Live X read and end-of-shift Z snapshot. Available from the terminal.',
    icon: BarChart3,
    action: 'Open terminal',
    href: '/pos',
  },
  {
    id: 'petty-cash',
    title: 'Petty Cash Reports',
    hint: 'Denominations, variance and cashier hand-over ledger.',
    icon: Wallet,
    action: 'Open petty cash reports',
    href: '/admin/petty-cash/reports',
  },
];

// ─── Advanced analytics — coming soon catalogue ───────────────────────
// Each card is a placeholder ONLY. No backend call, no calculated
// figure, no illustrative chart, no invented insight. Clicking opens a
// simple dialog stating that the report is planned for a future sprint.
const ADVANCED_ANALYTICS = [
  { id: 'menu-engineering',   title: 'Menu Engineering',                      icon: Utensils,       hint: 'Star / Plow-horse / Puzzle / Dog matrix — needs contribution margin + mix.' },
  { id: 'popularity',         title: 'Popularity Analysis',                   icon: Star,           hint: 'Item ranking by frequency and revenue, filterable by channel and daypart.' },
  { id: 'daypart',            title: 'Daypart Sales',                         icon: Sun,            hint: 'Breakfast / lunch / snack / dinner heat by weekday and outlet.' },
  { id: 'channel',            title: 'Channel-wise Sales',                    icon: Truck,          hint: 'Dine-in vs takeaway vs delivery vs room service, with per-channel margin.' },
  { id: 'day-wise',           title: 'Day-wise Sales',                        icon: CalendarDays,   hint: 'Business-day totals with variance vs 7-day rolling average.' },
  { id: 'weekday',            title: 'Weekday-wise Analysis',                 icon: CalendarClock,  hint: 'Monday-through-Sunday pattern with channel and category overlay.' },
  { id: 'calendar-special',   title: 'Calendar & Special Days Sales',         icon: Sparkles,       hint: 'Public holidays, festivals, matches and store events tagged and compared.' },
  { id: 'consumption-lakh',   title: 'Consumption per ₹1 Lakh Sales',         icon: IndianRupee,    hint: 'Ingredient and consumables cost normalised to every ₹1L of revenue.' },
  { id: 'forecast',           title: 'Sales Forecast · Historical Data',      icon: LineChart,      hint: 'Rolling-window forecast with confidence band. Needs seasonality model.' },
  { id: 'per-head',           title: 'Per Head Consumption',                  icon: UserRound,      hint: 'Average spend per cover, split by channel, guest type and outlet.' },
  { id: 'veg-nonveg',         title: 'Vegetarian vs Non-Vegetarian Ratio',    icon: Leaf,           hint: 'Item and revenue split with drill-down to category and outlet.' },
  { id: 'category-wise',      title: 'Category-wise Sales',                   icon: Grid3x3,        hint: 'Category contribution to revenue and cover count.' },
  { id: 'captain-wise',       title: 'Captain-wise Sales',                    icon: ShieldCheck,    hint: 'Steward and captain performance, upsell rate and average bill size.' },
  { id: 'kitchen-time',       title: 'Kitchen Preparation Time',              icon: Timer,          hint: 'Median and P90 KOT-to-served time by station and daypart.' },
  { id: 'auto-indent',        title: 'Auto Indent Guide',                     icon: TrendingUp,     hint: 'Suggested procurement based on rolling consumption and lead time.' },
];

function WorkingReportCard({ report, onOpen }) {
  const Icon = report.icon;
  return (
    <Card className="bg-white border-[#E2E8DF] hover:shadow-md transition-shadow"
          data-testid={`working-report-${report.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[color:var(--color-primary-tint)] flex items-center justify-center">
              <Icon className="w-4.5 h-4.5 text-[color:var(--color-primary)]" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[#1A2421]">{report.title}</h4>
              <Badge className="mt-1 bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-50 text-[10px]">
                Available now
              </Badge>
            </div>
          </div>
        </div>
        <p className="text-xs text-[#5C6B64] mt-3 min-h-[36px]">{report.hint}</p>
        <Button variant="outline" size="sm"
                onClick={() => onOpen(report)}
                className="mt-3 w-full border-[#E2E8DF] text-[#1A2421] hover:text-[color:var(--color-primary)]"
                data-testid={`working-report-open-${report.id}`}>
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> {report.action}
        </Button>
      </CardContent>
    </Card>
  );
}

function ComingSoonCard({ report, onOpen }) {
  const Icon = report.icon;
  return (
    <Card className="bg-white border-[#E2E8DF] hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onOpen(report)}
          data-testid={`analytics-card-${report.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#F4F6F2] flex items-center justify-center">
            <Icon className="w-4.5 h-4.5 text-[#3F4A47]" />
          </div>
          <Badge className="bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-50 text-[10px]"
                 data-testid={`analytics-badge-${report.id}`}>
            Coming Soon
          </Badge>
        </div>
        <h4 className="text-sm font-semibold text-[#1A2421] mt-3">{report.title}</h4>
        <p className="text-xs text-[#5C6B64] mt-1.5 min-h-[54px]">{report.hint}</p>
      </CardContent>
    </Card>
  );
}

export default function PosReports() {
  const navigate = useNavigate();
  const [planned, setPlanned] = useState(null);

  const openWorking = (r) => {
    if (r.href) navigate(r.href);
  };

  const openPlanned = (r) => setPlanned(r);

  return (
    <div data-testid="pos-reports">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <button className="inline-flex items-center gap-1.5 text-sm text-[#5C6B64] hover:text-[color:var(--color-primary)] mb-2"
                  onClick={() => navigate('/pos')}
                  data-testid="back-to-terminal">
            <ArrowLeft className="w-4 h-4" /> Back to terminal
          </button>
          <h2 className="text-lg sm:text-xl tracking-tight font-semibold text-[#1A2421] flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[color:var(--color-primary)]" /> POS Reports
          </h2>
          <p className="text-sm text-[#5C6B64] mt-1 max-w-2xl">
            The reports available in the current build stay unchanged and accessible from
            this page. The Advanced Analytics catalogue below is a roadmap of reports
            planned for a future analytics sprint — no data or calculations are shown yet.
          </p>
        </div>
      </div>

      {/* ─── Existing working reports ─── */}
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold mb-2.5">
          Available now
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {WORKING_REPORTS.map((r) => (
            <WorkingReportCard key={r.id} report={r} onOpen={openWorking} />
          ))}
        </div>
      </div>

      {/* ─── Advanced Analytics — Coming Soon ─── */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold">
              Advanced analytics
            </div>
            <h3 className="text-base font-semibold text-[#1A2421] mt-0.5">Coming soon</h3>
          </div>
          <Badge className="bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-50 text-[10px]">
            Planned for a future sprint
          </Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
             data-testid="advanced-analytics-catalogue">
          {ADVANCED_ANALYTICS.map((r) => (
            <ComingSoonCard key={r.id} report={r} onOpen={openPlanned} />
          ))}
        </div>
      </div>

      {/* Coming-soon detail dialog */}
      <Dialog open={!!planned} onOpenChange={(o) => !o && setPlanned(null)}>
        <DialogContent data-testid="analytics-coming-soon-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A2421]">{planned?.title}</DialogTitle>
            <DialogDescription className="text-[#5C6B64]">
              This report is planned for a future analytics sprint.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-[#E2E8DF] bg-[#F9F9F8] p-3 text-xs text-[#3F4A47]">
            <div className="font-medium text-[#1A2421] mb-1">What it will show</div>
            {planned?.hint}
          </div>
          <DialogFooter>
            <Button onClick={() => setPlanned(null)}
                    className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="analytics-coming-soon-close">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
